import type { JSX } from "react";
import { useSwipeable } from "react-swipeable";
import { FC, useState, useCallback } from "react";
import styled from "styled-components";
import { color as themeColor } from "@styles";
import { HeadingSize, H3 } from "../../atoms/Heading";
import { P1 } from "../../atoms/Paragraph";
import { Tab, HandleClick } from ".";
import { Image } from "../../atoms/Image";
import { StandardSection } from "../../templates/StandardSection";
import { tablet, mobile } from "components/templates/Breakpoints";

export const TabSwitcherMobile: FC<
  React.PropsWithChildren<{
    id?: string;
    tabs: Tab[];
    tabTitles: string[];
    handleClick: HandleClick;
    selectedTab: number;
    imageOnly?: boolean;
    clouds?: JSX.Element;
    pb?: string;
    footnote?: string;
  }>
> = ({ id, tabs, handleClick, selectedTab, imageOnly, clouds, pb }) => {
  const [isSwiping, setIsSwiping] = useState(false);

  const goLeft = useCallback(() => {
    setIsSwiping(true);
    handleClick(selectedTab - 1);
    setTimeout(() => {
      setIsSwiping(false);
    }, 400);
  }, [selectedTab, setIsSwiping, handleClick]);

  const goRight = useCallback(() => {
    setIsSwiping(true);
    handleClick(selectedTab + 1);
    setTimeout(() => {
      setIsSwiping(false);
    }, 400);
  }, [selectedTab, setIsSwiping, handleClick]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => goRight(),
    onSwipedRight: () => goLeft(),
    trackMouse: true,
    touchEventOptions: { passive: true },
  });
  const { title, heading, content, callToAction, url, footnote } = tabs[selectedTab];

  return (
    <MaxWidth {...swipeHandlers}>
      {clouds || null}
      <Relative>
        <div
          style={{
            cursor: "pointer",
            paddingTop: "2.5rem",
          }}
        >
          <Wrapper tabIndex={selectedTab}>
            {tabs.map(({ title, image, alt }, index) => (
              <ImageContainer key={title}>
                {image ? (
                  typeof image === "string" ? (
                    <Image path={image} alt={alt || ""} />
                  ) : (
                    image
                  )
                ) : (
                  <Placeholder />
                )}
              </ImageContainer>
            ))}
          </Wrapper>
          <LeftArrow
            isSwiping={isSwiping}
            tabIndex={0}
            onKeyPress={(e) => e.key === "Enter" && goLeft()}
            onClick={goLeft}
            role="button"
            disabled={selectedTab === 0}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="18"
              viewBox="0 0 10 18"
              fill="none"
              role="img"
              aria-labelledby="leftarrow-title"
            >
              <title id="leftarrow-title">Left Arrow</title>
              <path
                d="M9 1L1 9L9 17"
                stroke="#956AFF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </LeftArrow>
          <RightArrow
            isSwiping={isSwiping}
            tabIndex={0}
            onKeyPress={(e) => e.key === "Enter" && goRight()}
            onClick={goRight}
            role="button"
            disabled={selectedTab === tabs.length - 1}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="18"
              viewBox="0 0 10 18"
              fill="none"
              role="img"
              aria-labelledby="rightarrow-title"
            >
              <title id="rightarrow-title">Right Arrow</title>
              <path
                d="M1 17L9 9L0.999999 1"
                stroke="#956AFF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </RightArrow>
        </div>
      </Relative>
      <StandardSection pt="1rem" pb={pb}>
        <div aria-live="polite" aria-atomic aria-labelledby={`${id}-tabswitchermobile-title`}>
          <Label>{title}</Label>
          {imageOnly ? null : (
            <H3
              id={`${id}-tabswitchermobile-title`}
              size={HeadingSize.H2}
              pt="0.5rem"
              fontSize={["2.5rem"]}
            >
              {heading}
            </H3>
          )}
          <P1Section>
            <P1 align="left">{content}</P1>
          </P1Section>

          <BottomSection>
            {footnote && <Footnote>{footnote}</Footnote>}
            {imageOnly || !url ? null : (
              <FindOutMoreLink href={url}>{callToAction || "Find out more"}</FindOutMoreLink>
            )}
            {footnote ? <Footnote>{footnote}</Footnote> : null}

            <Progress>
              {tabs.map(({ title }, index) => (
                <span key={title} onClick={() => handleClick(index)}>
                  <Circle current={index === selectedTab} />
                </span>
              ))}
            </Progress>
          </BottomSection>
        </div>
      </StandardSection>
    </MaxWidth>
  );
};

const Circle = styled.div<{ current?: boolean }>`
  cursor: pointer;
  height: 1rem;
  width: 1rem;
  background: ${(p) => (p.current ? "#956AFF" : "#DFD3FF")};
  border-radius: 50%;
  display: inline-block;
  margin: 1rem 0.5rem 1.5rem;
`;

const IMAGE_HEIGHT = 37.5; // rem

const MaxWidth = styled.div`
  position: relative;
  margin-top: -0.5rem;
  max-width: 100vw;
  overflow-x: hidden;
  position: relative;
`;

const Relative = styled.div`
  position: relative;
`;

const Label = styled.div`
  padding: 0.5rem 1rem 0.5rem 1rem;
  margin-bottom: 0.5rem;
  background: #956aff;
  border-radius: 0.5rem;
  color: #ffffff;
  font-size: 0.875rem;
  line-height: 1rem;
  letter-spacing: 0.075rem;
  width: fit-content;
`;

const Arrow = styled.div<{ isSwiping?: boolean; disabled?: boolean }>`
  height: 100%;
  background: transparent;
  opacity: ${(p) => (p.disabled ? 0 : 0.5)};
  width: 5rem;
  position: absolute;
  cursor: pointer;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;
  transition: ${(p) => (p.isSwiping ? `100ms ease-out` : `400ms ease-out`)};

  &:hover {
    opacity: ${(p) => (p.disabled ? 0 : 1)};
  }
`;

const LeftArrow = styled(Arrow)`
  left: 0;
`;

const RightArrow = styled(Arrow)`
  right: 0;
`;

const Wrapper = styled.div<{ tabIndex: number }>`
  padding-bottom: 2rem;
  white-space: nowrap;
  transition: 200ms ease-in-out;
  ${(p) =>
    !p.tabIndex ? `transform: translateX(0)` : `transform: translateX(-${p.tabIndex * 100}%)`};
  cursor: pointer;

  &:focus {
    border: none;
    outline: none;
  }
`;

const ImageContainer = styled.div`
  display: inline-block;
  width: 100%;
  margin: 0 auto;
  border-radius: 0.5rem;
  pointer-events: none;

  svg,
  img,
  video {
    width: 100%;
    height: 100%;
    max-height: ${IMAGE_HEIGHT}rem;
    object-fit: contain;
  }

  video {
    border-radius: 0.5rem;
  }
`;

const Placeholder = styled.div`
  width: 100%;
  height: 100%;
  max-height: ${IMAGE_HEIGHT}rem;
  background: ${themeColor.S320};
  border-radius: 0.5rem;
`;

const Progress = styled.div`
  margin-top: 1.5rem;
  text-align: left;

  @media only screen and (max-width: 40rem) {
    text-align: center;
  }
`;

const BottomSection = styled.div`
  text-align: left;
`;

const FindOutMoreLink = styled.a`
  color: ${themeColor.P100};
  font-size: 1.25rem;
  letter-spacing: 0.075rem;
  line-height: 2rem;
  font-weight: 400;
  text-decoration-line: underline;
  text-align: left;
`;

const P1Section = styled.div`
  ${tablet} {
    min-height: 11rem;
  }

  ${mobile} {
    min-height: 16rem;
  }
`;

const Footnote = styled.p`
  color: ${themeColor.B2100};
  font-size: 1.25rem;
  letter-spacing: 0.075rem;
  line-height: 2rem;
  font-weight: 400;
  padding: 1rem 0;
`;
